import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Text,
  Tooltip,
  Link,
  useColorModeValue
} from '@chakra-ui/react';
import { processCitations } from '../../utils/citationUtils';

/**
 * CitationDisplay component that renders text containing citation markers like [1], [2]
 * and links them to corresponding citation entries.
 * 
 * @param {Object} props
 * @param {string} props.text - The text containing citation markers
 * @param {Array} props.citations - Array of citation objects
 */
const CitationDisplay = ({ text: textProp, citations }) => {
  // Use semantic colors from our theme
  const textColor = useColorModeValue('text.default', 'text.default');
  const superscriptColor = useColorModeValue('brand.primary', 'brand.accent');

  // Extract value if textProp is an object with value property, otherwise use textProp directly
  const actualText = (typeof textProp === 'object' && textProp !== null && 'value' in textProp)
    ? textProp.value
    : textProp;
  
  const processedCitations = useMemo(() => processCitations(citations || []), [citations]);

  // Ensure actualText is a string before proceeding.
  if (typeof actualText !== 'string') {
    console.warn('CitationDisplay: Expected text to be a string or an object with a value property, but received:', textProp);
    return <Text>{''}</Text>; // Render empty string if actualText is not a string
  }

  // If no actualText or processed citations, just return the plain actualText
  if (!actualText || processedCitations.length === 0) {
    return <Text>{actualText || ''}</Text>;
  }
  
  // Regular expression to match citation markers like [1], [2], etc.
  const citationRegex = /\[(\d+)\]/g;
  
  // Process the text to add hyperlinks for citation markers
  const segmentsWithLinks = useMemo(() => {
    const result = [];
    let lastIndex = 0;
    let match;
    
    // Create a new regex instance for the loop
    const regex = new RegExp(citationRegex);
    
    // Find all citation markers
    while ((match = regex.exec(actualText)) !== null) {
      const markerText = match[0]; // This is the full marker, e.g., "[1]"
      const startIndex = match.index;
      const endIndex = startIndex + markerText.length;
      
      // Add text before the citation marker
      if (startIndex > lastIndex) {
        result.push({
          type: 'text',
          content: actualText.substring(lastIndex, startIndex)
        });
      }
      
      // Find the corresponding citation by matching the markerText with the citation's id
      const citation = processedCitations.find(c => c.id === markerText);
      
      if (citation) {
        // Add linked citation marker
        result.push({
          type: 'citation',
          marker: match[0], // e.g., "[1]"
          citation // The actual citation object from the processed array
        });
      } else {
        // No valid citation found (e.g., marker [5] but only 3 processed citations), just add as text
        result.push({
          type: 'text',
          content: match[0]
        });
      }
      
      lastIndex = endIndex;
    }
    
    // Add any remaining text
    if (lastIndex < actualText.length) {
      result.push({
        type: 'text',
        content: actualText.substring(lastIndex)
      });
    }
    
    return result;
  }, [actualText, processedCitations]);
  
  // Render tooltip content for a citation
  const renderTooltipContent = (citation) => (
    <Box
      p={3}
      borderRadius="md"
      borderWidth="1px"
      borderColor="border.default"
      bg="bg.tooltip"
      boxShadow="md"
      maxW="300px"
    >
      <Text
        fontWeight="medium"
        mb={2}
        fontFamily="heading"
        borderBottomWidth="1px"
        borderColor="border.light"
        pb={1}
      >
        {citation.title}
      </Text>
      <Text
        fontSize="sm"
        fontFamily="body"
        color="text.muted"
      >
        {citation.referenceText || citation.snippet || 'No further details available.'}
      </Text>
    </Box>
  );
  
  return (
    <Text>
      {segmentsWithLinks.map((segment, index) => {
        if (segment.type === 'text') {
          return <React.Fragment key={index}>{segment.content}</React.Fragment>;
        } else if (segment.type === 'citation') {
          return (
            <Tooltip 
              key={index}
              label={renderTooltipContent(segment.citation)}
              placement="top"
              hasArrow
            >
              {/* Use either Link (if URL exists) or styled Box (if URL doesn't exist) */}
              {segment.citation.url ? (
                <Link
                  href={segment.citation.url}
                  isExternal
                  color={textColor}
                  _hover={{
                    textDecoration: 'none',
                    backgroundColor: 'rgba(100, 100, 255, 0.1)'
                  }}
                  display="inline"
                  borderRadius="sm"
                  px={1}
                >
                  <Box
                    as="span"
                    color={superscriptColor}
                    fontWeight="bold"
                    fontSize="xs"
                  >
                    {segment.marker}
                  </Box>
                </Link>
              ) : (
                <Box
                  as="span"
                  display="inline"
                  borderRadius="sm"
                  px={1}
                  color={superscriptColor}
                  fontWeight="bold"
                  fontSize="xs"
                  _hover={{
                    backgroundColor: 'rgba(100, 100, 255, 0.1)'
                  }}
                  cursor="pointer"
                >
                  {segment.marker}
                </Box>
              )}
            </Tooltip>
          );
        }
        return null;
      })}
    </Text>
  );
};

CitationDisplay.propTypes = {
  text: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      // groundingMetadata is not used by CitationDisplay but might be part of the object
      groundingMetadata: PropTypes.object
    })
  ]),
  citations: PropTypes.arrayOf(
    PropTypes.shape({
      // id is not strictly required to be a number by this component anymore if linking by index,
      // but the object structure from processCitations will have an id.
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      title: PropTypes.string.isRequired,
      url: PropTypes.string,
      referenceText: PropTypes.string, // AI sources have referenceText, user might use snippet or notes
      snippet: PropTypes.string // Tooltip currently uses snippet
    })
  )
};

CitationDisplay.defaultProps = {
  text: '', // Default to empty string, will be handled by the component
  citations: []
};

export default CitationDisplay;
